import { v4 as uuidV4 } from 'uuid';
import { roles as bronteRoles } from 'subscription/constants';
import CoursewareItemProvider from 'services/coursewareItem/CoursewareItemProvider';
import * as browserStorage from '../browserStorage';
import { SESSION_STORAGE, storageKeys } from '../browserStorage/constants';

export const { USER_PARAMS } = storageKeys;
export const PI_IES_PERSON_ID_TYPE = 'PI';

export default (() => {
  /**
   * Method to push events to GTM dataLayer
   * @param data - the event object that will be pushed to dataLayer
   */
  const _pushGTMEvent = (data) => {
    try {
      window.dataLayer.push(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Failed to fire GTM event');
    }
  };

  /**
   * Method to check if user info is available in cache and push to dataLayer
   */
  const _pushUserInfoFromSessionStorage = () => {
    const userParamString = browserStorage.load(USER_PARAMS, SESSION_STORAGE);
    if (userParamString) {
      const userParam = JSON.parse(userParamString);
      if (userParam) {
        _pushGTMEvent(userParam);
      }
    }
  };

  /**
   * Method to push browsers global parameters
   * @param browserGlobalParams - clients browser information
   */
  const pushGlobalParams = (browserGlobalParams) => {
    _pushGTMEvent(browserGlobalParams);
  };

  /**
   * Bronte roles to GTM roles
   * @param roles - One of INSTRUCTOR, AERO_INSTRUCTOR, STUDENT, STUDENT_GUEST, DEVELOPER, ADMIN, SUPPORT
   * @returns {string} - One of ["Student", "Instructor", "TeachingAssistant", "Parent", "Administrator","Employee", "Other"]
   */
  const getPearsonRoleCode = (roles = []) => {
    if (roles.includes(bronteRoles.SUPPORT)) {
      return 'Employee'; // Make consts in file
    } if (roles.includes(bronteRoles.ADMIN)) {
      return 'Administrator';
    } if (roles.includes(bronteRoles.DEVELOPER)) {
      return 'TeachingAssistant';
    } if (roles.includes(bronteRoles.AERO_INSTRUCTOR)) {
      return 'Instructor';
    } if (roles.includes(bronteRoles.STUDENT || roles.includes(bronteRoles.STUDENT_GUEST))) {
      return 'STUDENT';
    }
    return 'OTHER';
  };

  const _getPiSessionInfo = () => {
    let personId;
    let personIdType;
    let sessionId;
    if (window.piSession && window.piSession.userId()) {
      personId = window.piSession.userId();
      personIdType = PI_IES_PERSON_ID_TYPE;
      sessionId = window.piSession.getContextId();
    }
    return { personId, personIdType, sessionId };
  };

  /**
   * Method to push user params on successful authentication
   * @param accountData - users account information
   */
  const pushUserParams = (accountData) => {
    const { personId, personIdType, sessionId } = _getPiSessionInfo();
    const userParams = {
      personId,
      personIdType,
      personRoleCode: getPearsonRoleCode(accountData.roles),
      sessionId,
      organizationId: accountData.subscriptionId,
    };
    _pushGTMEvent(userParams);
    browserStorage.save(USER_PARAMS, JSON.stringify(userParams), SESSION_STORAGE);
  };

  /**
   * Method to get current local time
   * @returns {Promise<string>} - Current local timestamp
   */
  const getCurrentLocalTime = () => new Date(`${new Date().toString().split('GMT')[0]} UTC`)
    .toISOString()
    .replace('Z', '');

  /**
   * Method to push each & every page rendering after the user’s authentication.
   * @param pageObj
   * @param pageErrors - list of page error where PageError is {errorCode: '',errorUrl: '', errorMessage: '', errorStateCode: '', errorDetail: ''}
   */
  const pushPageViewEvent = ({
    pageName = window.location.pathName,
    contentId,
    contentType,
    courseSectionId,
    errorCode,
    errorMessage,
    errorDetail,
  }) => {
    const productId = CoursewareItemProvider.getProductId() || 'NA';
    const { cohortId, deploymentId } = CoursewareItemProvider.getDeploymentContext();
    const pageErrors = [{
      errorCode,
      errorStateCode: errorCode,
      errorMessage,
      errorDetail: errorDetail || errorMessage,
    }];

    try {
      const pageViewEventObj = {
        event: 'pageview',
        pageName, // Page Name -> Interactive Name
        productId,
        productPlatformCode: 'BRONTE', // Product Platform, Ex: GLP, ILP, MASTERING
        productModelName: 'BRONTE', // Name of Product, Ex: REVEL, ETEXT2_PXE, ETEXT2_CITE
        courseId: cohortId, // Course ID -> Cohort ID
        courseSectionId: courseSectionId || deploymentId, // Course Section ID -> Pathway ID
        contentId, // Content ID -> Activity/Interactive ID
        contentType, // Type of the Content -> ACTIVITY/INTERACTIVE
        messageId: uuidV4(), // UUID
        transactionDt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
        transactionLocalDt: getCurrentLocalTime(),
        pageErrors,
      };
      _pushUserInfoFromSessionStorage();
      _pushGTMEvent(pageViewEventObj);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Failed to push Page event');
    }
  };

  const pushPageViewErrorEvent = ({
    contentId,
    contentType,
    errorCode,
    error,
  }) => {
    const { message, path } = error;
    pushPageViewEvent({
      contentId,
      contentType,
      errorCode,
      errorMessage: message,
      errorDetail: path.toString(),
    });
  };

  /**
   * Method to push user telemetry page activity event, once per page
   */
  const pushUserTelemetryPageActivity = () => {
    const initTelemetryEvent = {
      event: 'inittelemetry',
    };
    _pushUserInfoFromSessionStorage();
    _pushGTMEvent(initTelemetryEvent);
  };

  /**
   * Method to push users logout event
   */
  const pushUserLogoutEvent = () => {
    const logoutEvent = {
      event: 'logout',
      messageId: uuidV4(), // UUID v4
      transactionDt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
    };
    _pushGTMEvent(logoutEvent);
    browserStorage.remove(USER_PARAMS, SESSION_STORAGE);
  };

  const pushTelemetryPageError = (errorCode, errorMessage) => {
    const errorUrl = window.location;
    const pageError = {
      event: 'telemetrypageerror',
      errorCode,
      errorUrl,
      errorMessage,
      errorStateCode: errorCode,
      errorDetail: errorMessage,
      transactionDt: new Date().toISOString(),
    };
    _pushGTMEvent(pageError);
  };

  return {
    pushGlobalParams,
    pushUserParams,
    pushPageViewEvent,
    pushPageViewErrorEvent,
    pushUserTelemetryPageActivity,
    pushUserLogoutEvent,
    pushTelemetryPageError,
  };
})();
